import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Store, StoresApiService } from '@client-monorepo/daily-fintech/stores';
import { PageLayoutComponent } from '@client-monorepo/common/ui-components';
import { NgxBadgeModule } from '@digipay/ngx-badge';
import { StoreOverviewComponent } from '../../components/store-overview/store-overview.component';
import { StoreIntroComponent } from '../../components/store-intro/store-intro.component';
import { ApiImageModule } from '@digipay/ng-ui-api-image';
import { StoreProductsComponent } from '../../components/store-products/store-products.component';
import { DpIconComponent } from '@client-monorepo/common/icon';
import { ActionHandlerService, ActionType, RedirectionTypeEnum } from '@client-monorepo/common/action-handler';
import { NgxBottomNavigationService } from '@digipay/ngx-bottom-navigation';
import { BackHandlerService } from '@client-monorepo/back-handler';
import { NgxBottomSheetService } from '@digipay/ngx-bottom-sheet';
import { Subscription } from 'rxjs';
import { Rateable, RateApiService, RateBaseComponent, RateCountComponent, RateService } from '@client-monorepo/common/rate';

@Component({
  selector: 'stores-applet-store',
  standalone: true,
  imports: [
    CommonModule,
    PageLayoutComponent,
    NgxBadgeModule,
    StoreOverviewComponent,
    StoreIntroComponent,
    ApiImageModule,
    StoreProductsComponent,
    DpIconComponent,
    RateCountComponent,
  ],
  templateUrl: './store.component.html',
  styleUrl: './store.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StoreComponent implements OnInit, OnDestroy {
  title = signal<string>('');
  store = signal<Store | undefined>(undefined);

  activatedRoute = inject(ActivatedRoute);
  location = inject(Location);
  storeApi = inject(StoresApiService);
  router = inject(Router);
  actionHandler = inject(ActionHandlerService);
  bottomNavigationService = inject(NgxBottomNavigationService);
  backHandler = inject(BackHandlerService);
  rateService = inject(RateService);
  rateApi = inject(RateApiService);
  bottomSheetService = inject(NgxBottomSheetService);
  subs = new Subscription();
  isDisabled = computed(() => this.store()?.state?.disabled);

  ngOnInit(): void {
    this.loadRateables();
    this.improveUrlReadability();
    this.getStore();
    this.bottomNavigationService.hide();
  }

  private loadRateables(): void {
    this.rateApi.getAllRatables().subscribe({
      next: (res) => {
        this.rateService.setRatables$(res.rateableList);
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  checkIfRateAvailable(): void {
    const sub = this.rateService.getRatables$().subscribe({
      next: (ratables) => {
        if (!ratables?.length) {
          return;
        }
        const showRate = this.rateService.checkIfRateAvailable(this.store());
        const currentRatable: Rateable | null = this.rateService.getCurrentRatable();
        if (showRate) {
          this.bottomSheetService.openBottomSheet(RateBaseComponent, {
            storeImage: this.store()?.logoImageId ?? '',
            title: this.store()?.title ?? '',
            gateway: currentRatable?.purchase.activityPaymentChannel,
          });
          const closeSub = this.bottomSheetService.onClose.subscribe(() => {
            const output = this.bottomSheetService.outputData();
            this.bottomSheetService.outputData.set(null);
            if (output && output.sentRate) {
              this.loadRateables();
            }
            closeSub.unsubscribe();
          });
        }
      },
    });
    this.subs.add(sub);
  }

  improveUrlReadability(): void {
    this.title.set(decodeURI(this.activatedRoute.snapshot.paramMap.get('title') as string));
    this.actionHandler.handle({
      type: ActionType.REDIRECT,
      payload: { url: 'stores/' + this.title(), type: RedirectionTypeEnum.auto, replaceUrl: true },
    });
  }

  getStore(): void {
    this.storeApi.getStore(this.title()).subscribe({
      next: (store) => {
        this.store.set(store);
        this.checkIfRateAvailable();
      },
    });
  }

  goBack(): void {
    this.backHandler.goBack();
  }

  ngOnDestroy(): void {
    this.bottomNavigationService.show();
    this.subs.unsubscribe();
    this.rateService.destroy();
  }
}
